/**
 * Filtros avançados (multi-seleção) para Cotações e Pedidos
 * - Busca textual (número, título)
 * - Multi-select: status, fornecedor, projeto, item
 * - Toggle urgentes
 * - Datas: personalizado / mês / trimestre / semestre
 */
import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Search, X, Filter, AlertTriangle, Calendar as CalendarIcon, Check, Coins } from 'lucide-react';

export const PERIODOS = [
  { id: 'todos',       label: 'Todos os períodos' },
  { id: 'mes',         label: 'Mês atual' },
  { id: 'trimestre',   label: 'Trimestre atual' },
  { id: 'semestre',    label: 'Semestre atual' },
  { id: 'personalizado', label: 'Personalizado' },
];

/** Calcula data início/fim para presets de período. */
export const computeDateRange = (preset, customInicio, customFim) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-11
  if (preset === 'mes') {
    return {
      inicio: new Date(y, m, 1),
      fim: new Date(y, m + 1, 0, 23, 59, 59),
    };
  }
  if (preset === 'trimestre') {
    const trimStart = Math.floor(m / 3) * 3;
    return {
      inicio: new Date(y, trimStart, 1),
      fim: new Date(y, trimStart + 3, 0, 23, 59, 59),
    };
  }
  if (preset === 'semestre') {
    const semStart = m < 6 ? 0 : 6;
    return {
      inicio: new Date(y, semStart, 1),
      fim: new Date(y, semStart + 6, 0, 23, 59, 59),
    };
  }
  if (preset === 'personalizado') {
    return {
      inicio: customInicio ? new Date(customInicio + 'T00:00:00') : null,
      fim: customFim ? new Date(customFim + 'T23:59:59') : null,
    };
  }
  return { inicio: null, fim: null };
};

/** Multi-select com chips. Genérico para qualquer lista. */
const MultiSelectPopover = ({ label, options, selectedIds, onChange, testId, icon: Icon = Filter, searchable = false }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    if (!searchable || !search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(o => (o.label || '').toLowerCase().includes(q));
  }, [options, search, searchable]);
  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter(x => x !== id));
    else onChange([...selectedIds, id]);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-9 ${selectedIds.length > 0 ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-white'}`}
          data-testid={testId}
        >
          <Icon className="w-3.5 h-3.5 mr-1.5" />
          {label}
          {selectedIds.length > 0 && (
            <Badge variant="secondary" className="ml-1.5 px-1.5 py-0 text-[10px] bg-emerald-600 text-white hover:bg-emerald-600">
              {selectedIds.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        {searchable && (
          <div className="relative mb-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar..."
              className="pl-7 h-8 text-xs"
            />
          </div>
        )}
        <div className="max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-xs text-gray-500 text-center py-3">Nenhum item</p>
          ) : (
            filtered.map(opt => {
              const sel = selectedIds.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggle(opt.id)}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded flex items-center justify-between hover:bg-gray-100 ${sel ? 'bg-emerald-50' : ''}`}
                  data-testid={`${testId}-opt-${opt.id}`}
                >
                  <span className="truncate flex-1">{opt.label}</span>
                  {sel && <Check className="w-3.5 h-3.5 text-emerald-600 ml-1 shrink-0" />}
                </button>
              );
            })
          )}
        </div>
        {selectedIds.length > 0 && (
          <div className="border-t pt-1 mt-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="text-[11px] text-gray-500 hover:text-red-600 w-full text-center py-1"
            >
              Limpar seleção
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

/** Painel principal de filtros — datas + (opcional) avançados. */
const ComprasFilters = ({
  showAdvanced = true,              // false para dashboard (apenas datas)
  searchTerm, setSearchTerm,
  // Multi-select selecionados
  statusSel = [], setStatusSel = () => {},
  fornecedorSel = [], setFornecedorSel = () => {},
  projetoSel = [], setProjetoSel = () => {},
  itemSel = [], setItemSel = () => {},
  moedaSel = [], setMoedaSel = () => {},
  urgenteSel = false, setUrgenteSel = () => {},
  // Opções
  statusOptions = [],               // [{id, label}]
  fornecedorOptions = [],
  projetoOptions = [],
  itemOptions = [],
  moedaOptions = [],
  // Datas
  periodoPreset, setPeriodoPreset,  // 'todos' | 'mes' | 'trimestre' | 'semestre' | 'personalizado'
  dataInicio, setDataInicio,        // YYYY-MM-DD (custom)
  dataFim, setDataFim,
  searchPlaceholder = 'Buscar...',
}) => {
  const limparTodos = () => {
    setSearchTerm && setSearchTerm('');
    setStatusSel([]); setFornecedorSel([]); setProjetoSel([]); setItemSel([]);
    setMoedaSel([]);
    setUrgenteSel(false);
    setPeriodoPreset && setPeriodoPreset('todos');
    setDataInicio && setDataInicio('');
    setDataFim && setDataFim('');
  };
  const algumFiltroAtivo = (
    (searchTerm || '').trim() ||
    statusSel.length || fornecedorSel.length || projetoSel.length || itemSel.length ||
    moedaSel.length ||
    urgenteSel || (periodoPreset && periodoPreset !== 'todos')
  );

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="compras-filters">
      {/* Busca textual — destaque à esquerda */}
      {setSearchTerm && (
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            value={searchTerm || ''}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 h-9 bg-white"
            data-testid="filter-search-input"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              aria-label="Limpar busca"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Separador visual sutil entre busca e demais filtros */}
      {setSearchTerm && showAdvanced && (
        <div className="hidden md:block h-6 w-px bg-slate-200" aria-hidden="true" />
      )}

      {/* Grupo de filtros — alinhados em "pills" */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Período (presets) — sempre presente */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`h-9 ${(periodoPreset && periodoPreset !== 'todos') ? 'border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100' : 'bg-white'}`}
              data-testid="filter-periodo-btn"
            >
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              {(PERIODOS.find(p => p.id === periodoPreset) || PERIODOS[0]).label}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-60 p-2" align="start">
            <div className="space-y-0.5">
              {PERIODOS.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPeriodoPreset(p.id)}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-100 ${periodoPreset === p.id ? 'bg-emerald-50 font-medium text-emerald-800' : ''}`}
                  data-testid={`filter-periodo-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {periodoPreset === 'personalizado' && (
              <div className="border-t pt-2 mt-2 space-y-2">
                <div>
                  <label className="text-[10px] text-gray-600 uppercase">De</label>
                  <Input
                    type="date"
                    value={dataInicio || ''}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="filter-data-inicio"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-600 uppercase">Até</label>
                  <Input
                    type="date"
                    value={dataFim || ''}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="filter-data-fim"
                  />
                </div>
              </div>
            )}
          </PopoverContent>
        </Popover>

        {showAdvanced && (
          <>
            <MultiSelectPopover
              label="Status"
              options={statusOptions}
              selectedIds={statusSel}
              onChange={setStatusSel}
              testId="filter-status"
            />
            <MultiSelectPopover
              label="Fornecedor"
              options={fornecedorOptions}
              selectedIds={fornecedorSel}
              onChange={setFornecedorSel}
              testId="filter-fornecedor"
              searchable
            />
            <MultiSelectPopover
              label="Projeto"
              options={projetoOptions}
              selectedIds={projetoSel}
              onChange={setProjetoSel}
              testId="filter-projeto"
              searchable
            />
            <MultiSelectPopover
              label="Item"
              options={itemOptions}
              selectedIds={itemSel}
              onChange={setItemSel}
              testId="filter-item"
              searchable
            />
            {moedaOptions.length > 1 && (
              <MultiSelectPopover
                label="Moeda"
                options={moedaOptions}
                selectedIds={moedaSel}
                onChange={setMoedaSel}
                testId="filter-moeda"
                icon={Coins}
              />
            )}
            <Button
              variant={urgenteSel ? 'default' : 'outline'}
              size="sm"
              className={`h-9 ${urgenteSel ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-white text-slate-700 hover:bg-red-50 hover:text-red-700 hover:border-red-300'}`}
              onClick={() => setUrgenteSel(!urgenteSel)}
              data-testid="filter-urgente"
              title="Mostrar apenas urgentes"
            >
              <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
              Urgente
            </Button>
          </>
        )}
      </div>

      {/* Limpar — empurrado para a direita */}
      {algumFiltroAtivo ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={limparTodos}
          className="h-9 text-gray-500 hover:text-red-600 hover:bg-red-50 ml-auto"
          data-testid="filter-limpar-todos"
        >
          <X className="w-3.5 h-3.5 mr-1" /> Limpar filtros
        </Button>
      ) : null}
    </div>
  );
};

export default ComprasFilters;
